import React from 'react'
import { useIntl } from 'react-intl'
import { useLocation, useParams, useSearchParams } from 'react-router-dom'
import { capitalize } from 'lodash'
import { toast } from 'react-toastify'
import styled from '@emotion/styled'
import LinearProgress from '@mui/material/LinearProgress'
import Breadcrumb from '../../../../components/Breadcrumb'
import { courseSubjectApiSlice } from '../../../../features/course/courseSubjectSlice'
import { H3 } from '../../../../layouts/TextStyles'
import ActiveCourseSubjectsTable from '../components/ActiveCourseSubjectsTable'
import InactiveCourseSubjectsTable from '../components/InactiveCourseSubjectsTable'

const CourseSubjectsPage = () => {
    const { courseId } = useParams()
    const { state } = useLocation()
    const { formatMessage, locale } = useIntl()
    const {
        data: activeCourseSubjectsData = [],
        isFetching,
        refetch,
    } = courseSubjectApiSlice.useFetchActiveCourseSubjectsQuery(
        Number(courseId),
    )
    const {
        data: inactiveCourseSubjectsData = [],
        isFetching: inactiveFetching,
    } = courseSubjectApiSlice.useFetchInactiveCourseSubjectsQuery(
        Number(courseId),
        { skip: !courseId },
    )
    const [deleteCourseSubject] =
        courseSubjectApiSlice.useDeleteCourseSubjectMutation()
    const [searchParams, setSearchParams] = useSearchParams('')

    const handleDelete = async (id: number) => {
        await toast.promise(deleteCourseSubject(id).unwrap(), {
            pending: 'Deleting',
            success: 'Delete successful',
            error: 'Could not delete item',
        })
    }

    const filteredData = () => {
        const group = searchParams.get('group')

        if (group) {
            return activeCourseSubjectsData.filter((item) =>
                item.languages.includes(group),
            )
        }

        return activeCourseSubjectsData
    }

    const refreshActiveCourseSubjects = () => refetch()

    return (
        <Wrapper>
            <Breadcrumb
                links={[
                    formatMessage({ id: 'courses' }),
                    state?.course?.[`title${capitalize(locale)}`],
                ]}
            />

            <Content>
                <Header>
                    <Title>
                        <H3>{formatMessage({ id: 'subjects' })}</H3>
                    </Title>

                    <Tabs>
                        <Tab
                            searchParams={searchParams.get('group')}
                            group="ALL"
                            onClick={() => setSearchParams('')}
                        >
                            {formatMessage({ id: 'allGroups' })}
                        </Tab>

                        <Tab
                            searchParams={searchParams.get('group')}
                            group="KZ"
                            onClick={() => setSearchParams('group=KZ')}
                        >
                            {formatMessage({ id: 'kazakhGroup' })}
                        </Tab>

                        <Tab
                            searchParams={searchParams.get('group')}
                            group="RU"
                            onClick={() => setSearchParams('group=RU')}
                        >
                            {formatMessage({ id: 'russianGroup' })}
                        </Tab>
                    </Tabs>
                </Header>

                {isFetching && <LinearProgress />}

                {activeCourseSubjectsData && (
                    <ActiveCourseSubjectsTable
                        activeCourseSubjectsData={filteredData()}
                        refresh={refreshActiveCourseSubjects}
                        onDelete={handleDelete}
                        course={state?.course}
                    />
                )}

                <Header style={{ marginTop: '16px' }}>
                    <Title>
                        <H3>{formatMessage({ id: 'inactiveSubjects' })}</H3>
                    </Title>
                </Header>

                {inactiveFetching && <LinearProgress />}

                {inactiveCourseSubjectsData && (
                    <InactiveCourseSubjectsTable
                        inactiveCourseSubjectsData={inactiveCourseSubjectsData}
                    />
                )}
            </Content>
        </Wrapper>
    )
}

export default CourseSubjectsPage

const Wrapper = styled.div`
    margin: 10px;
    border-radius: 16px;
`
const Title = styled.div`
    margin-bottom: 20px;
    text-align: left;
    display: flex;
    align-items: center;
`

const Content = styled.div`
    background-color: #fff;
    border-radius: 16px;
    padding: 20px;
    min-height: 80vh;
`

const Header = styled.header`
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
`

const Tabs = styled.div`
    display: flex;
    background: #f1f3f5;
    height: 24px;
    padding: 4px;
    border-radius: 8px;
    align-items: center;
    cursor: pointer;
`

const Tab = styled.div<{ searchParams: string; group: string }>`
    background: ${(props) =>
        props.group == 'ALL' && props.searchParams == null
            ? '#FFFFFF'
            : props.searchParams == props.group
            ? '#FFFFFF'
            : '#F1F3F5'};
    border-radius: 5px;
    padding: 4px 20px;
    color: ${(props) =>
        props.group == 'ALL' && props.searchParams == null
            ? '#404A51'
            : props.searchParams == props.group
            ? '#404A51'
            : '#AABBC7'}; ;
`
